/**
 * O QUE UM DIÁLOGO PRECISA FAZER ALÉM DE APARECER — `T8.5`, `T8.18`, `T8.26`.
 *
 * Três achados do relatório dizem a mesma frase por três telas: **Esc não fecha**. E um deles conta o
 * que isso custa: *"A única saída é clicar na imagem, o que a salva em Personagens, mas nada na tela
 * avisa isso."* Um diálogo sem saída não é um diálogo — é uma armadilha que ensina a pessoa a sair
 * pela porta errada.
 *
 * ── AS TRÊS COISAS, E POR QUE AS TRÊS E NÃO UMA ─────────────────────────────────────────────
 *
 *   · **Esc fecha.** É o gesto que existe em todo programa, e quem o aprendeu em outro lugar já o
 *     traz na mão.
 *   · **Clique fora fecha.** É o gesto de quem nunca ouviu falar de Esc — e é o que uma criança
 *     tenta primeiro.
 *   · **A página atrás não rola.** Rolar a página de trás enquanto o diálogo está aberto é o sintoma
 *     que faz a pessoa achar que o diálogo sumiu; e foi o que o `T8.5` relatou por último.
 *
 * ── UMA COISA QUE ESTE ARQUIVO DELIBERADAMENTE NÃO FAZ ──────────────────────────────────────
 *
 * Não prende o foco dentro do diálogo. Prender foco mal feito é pior que não prender: Tab para de
 * andar e a pessoa fica sem saída de teclado, que é exatamente o defeito que estamos consertando.
 * Fica escrito aqui como trabalho separado, com o gesto de Esc já garantindo a saída.
 */
import { useEffect } from "react";

/**
 * Liga Esc e travamento da rolagem de fundo enquanto o diálogo está montado.
 *
 * `fechar` entra na lista de dependências: um manipulador que fecha sobre a versão antiga da função
 * chamaria o fechamento de um estado que já não existe.
 */
/*
 * O NOME COMECA COM `use` EM INGLES, E NAO POR GOSTO: a regra `react-hooks/rules-of-hooks` reconhece
 * gancho pelo PREFIXO do nome, e ela so conhece `use`. Com `usarDialogo`, o `eslint` recusa o build
 * inteiro — *"React Hook useEffect is called in function that is neither a React function component
 * nor a custom React Hook function"*. E a mesma razao do `useTituloDaAba`, e esta e a segunda vez que
 * este produto paga por ela.
 */
export function useDialogo(fechar: () => void) {
  useEffect(() => {
    function aoTeclar(e: KeyboardEvent) {
      // `e.key` e não `keyCode`: `keyCode` está obsoleto e difere entre navegadores.
      if (e.key === "Escape") {
        e.stopPropagation();
        fechar();
      }
    }
    document.addEventListener("keydown", aoTeclar);

    // A rolagem do fundo: guardo o valor anterior em vez de assumir `""`, porque outra tela pode já
    // tê-lo definido — e devolver o valor errado deixaria a página travada depois de fechar.
    const anterior = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", aoTeclar);
      document.body.style.overflow = anterior;
    };
  }, [fechar]);
}

/**
 * O manipulador do clique fora, para o elemento de FUNDO.
 *
 * `e.target === e.currentTarget` é o que distingue "clicou no fundo" de "clicou em algo dentro do
 * diálogo e o evento subiu até aqui". Sem essa comparação, clicar em qualquer botão do diálogo o
 * fecharia — um conserto que produz um defeito pior que o original.
 */
export function aoClicarNoFundo(fechar: () => void) {
  return (e: { target: unknown; currentTarget: unknown }) => {
    if (e.target === e.currentTarget) fechar();
  };
}
